import { ApiError } from '@core/api/apiError';

/**
 * Номер, как его ввёл человек («8 (999) 123-45-67», «+7 999 123 45 67», «9991234567»), → E.164
 * «+79991234567». Сервер принимает только цифры с необязательным «+» (без пробелов и скобок), а код
 * звонка хранится по нормализованному номеру — поэтому номер для звонка и для подтверждения кода
 * должен быть одним и тем же. null — номер явно неполный или лишний.
 */
export function normalizePhoneInput(raw: string): string | null {
  const trimmed = raw.trim();
  const digits = trimmed.replace(/\D/g, '');
  let e164: string;
  if (trimmed.startsWith('+')) {
    e164 = `+${digits}`;
  } else if (digits.length === 11 && (digits[0] === '8' || digits[0] === '7')) {
    e164 = `+7${digits.slice(1)}`;
  } else if (digits.length === 10 && digits[0] === '9') {
    e164 = `+7${digits}`;
  } else {
    e164 = `+${digits}`;
  }
  return /^\+[1-9]\d{9,14}$/.test(e164) ? e164 : null;
}

/** +79991234567 → «+7 999 123-45-67»; прочие номера — как есть, с «+». */
export function formatPhone(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('7')) {
    return `+7 ${digits.slice(1, 4)} ${digits.slice(4, 7)}-${digits.slice(7, 9)}-${digits.slice(9)}`;
  }
  return phone.startsWith('+') ? phone : `+${phone}`;
}

/** Пауза перед повторным звонком (сервер: 60 с на номер, CODE_COOLDOWN). */
export const PHONE_CALL_RESEND_SECONDS = 60;

const CALL_UNAVAILABLE = 'Звонки с кодом сейчас недоступны. Попробуйте позже.';

/** Понятные тексты для кодов ошибок подтверждения номера (звонок с кодом, контакт Telegram, привязка). */
const PHONE_ERROR_MESSAGES: Record<string, string> = {
  CODE_INVALID: 'Неверный код. Введите последние 4 цифры номера, с которого вам позвонили.',
  INVALID_CODE: 'Код — это 4 цифры.',
  CODE_NOT_FOUND: 'Код устарел. Запросите новый звонок.',
  CODE_ATTEMPTS_EXCEEDED: 'Слишком много неверных попыток для этого звонка. Запросите новый звонок.',
  PHONE_LOCKED_OUT: 'Слишком много неверных попыток. Попробуйте через 30 минут.',
  CODE_COOLDOWN: 'Звонок уже отправлен. Позвонить ещё раз можно через минуту.',
  RATE_LIMIT_EXCEEDED: 'Слишком много попыток. Подождите минуту и попробуйте снова.',
  INVALID_PHONE: 'Проверьте номер — например, +7 999 123-45-67.',
  VALIDATION_ERROR: 'Проверьте номер — например, +7 999 123-45-67.',
  PHONE_VERIFICATION_UNAVAILABLE: CALL_UNAVAILABLE,
  PLUSOFON_NOT_CONFIGURED: CALL_UNAVAILABLE,
  PHONE_PROOF_INVALID: 'Telegram не подтвердил номер. Попробуйте ещё раз или введите номер вручную.',
  PHONE_PROOF_FOREIGN: 'Это контакт другого аккаунта Telegram. Поделитесь своим номером или введите его вручную.',
  PHONE_MISMATCH: 'Номер не совпадает с контактом Telegram.',
  PHONE_PROOF_REQUIRED: 'Подтвердите номер: поделитесь контактом или введите код из звонка.',
  // POST /auth/telegram/link: привязка по номеру на сервере не подключена.
  PHONE_LINK_UNAVAILABLE:
    'Найти карточку по номеру сейчас не получается. Нажмите «Пропустить» — номер можно привязать позже в «Моей карте».',
  // Старый бэкенд (до переноса истории): номер уже у карточки PWA этого заведения.
  PHONE_TAKEN_BY_PWA:
    'Этот номер уже привязан к карточке, которой вы пользуетесь через браузер. Чтобы объединить карточки, обратитесь на кассу.',
  INVALID_TELEGRAM: 'Не удалось подтвердить вход через Telegram. Закройте приложение и откройте его снова.',
  NETWORK_ERROR: 'Нет связи с сервером. Проверьте интернет и попробуйте снова.',
};

const FALLBACK_MESSAGE = 'Не получилось. Попробуйте ещё раз.';

/** Код ошибки сервера, если это ApiError. */
export function apiErrorCode(err: unknown): string | null {
  return err instanceof ApiError ? err.code : null;
}

/**
 * Текст ошибки для экрана номера: известный код — свой текст; иначе сообщение сервера, если оно
 * по-русски (часть сообщений бэкенда — английские технические), иначе общий текст.
 */
export function phoneErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const known = PHONE_ERROR_MESSAGES[err.code];
    if (known) return known;
    if (err.status === 429) return PHONE_ERROR_MESSAGES.RATE_LIMIT_EXCEEDED;
    if (/[А-Яа-яЁё]/.test(err.message)) return err.message;
  }
  return FALLBACK_MESSAGE;
}
